/**
 * The site form, used full-page by New site and as the edit panel on a site's
 * own page (no modal: a site is a record).
 *
 * Numbers are typed and sent in the tenant's units (`meta.units`): the API
 * converts, nothing here does. A blank number is sent as null (absent), never
 * as 0. On edit only the keys that changed are sent; PATCH keeps the rest.
 *
 * A pipeline has a line number, OD / WT / grade / MOP; a facility has a
 * facility type. The other kind's fields are hidden, not cleared.
 */
import React, { useMemo, useState } from 'react';
import ProvinceSelect from '../../components/ProvinceSelect';
import { blankToNull, toInput } from '../../lib/pipeline-facility';
import type { PfCatalog, PfMeta, PfSite, PfSiteWrite } from '../../types/pipeline-facility';
import { PF_INPUT, PF_PRIMARY, PF_SECONDARY, PfField, PfFormError } from './PfBits';

interface FormState {
  customer_id: string;
  kind: string;
  name: string;
  status: string;
  licence_number: string;
  line_number: string;
  lsd: string;
  province_state: string;
  latitude: string;
  longitude: string;
  product: string;
  sour: boolean;
  od: string;
  wt: string;
  grade: string;
  mop: string;
  facility_type: string;
  notes: string;
}

const fromSite = (s: PfSite | null, defaultKind: string): FormState => ({
  customer_id: s?.customerId ?? '',
  kind: s?.kind ?? defaultKind,
  name: s?.name ?? '',
  status: s?.status ?? 'active',
  licence_number: s?.licenceNumber ?? '',
  line_number: s?.lineNumber ?? '',
  lsd: s?.lsd ?? '',
  province_state: s?.provinceState ?? '',
  latitude: toInput(s?.latitude),
  longitude: toInput(s?.longitude),
  product: s?.product ?? '',
  sour: s?.sour === true,
  od: toInput(s?.od),
  wt: toInput(s?.wt),
  grade: s?.grade ?? '',
  mop: toInput(s?.mop),
  facility_type: s?.facilityType ?? '',
  notes: s?.notes ?? '',
});

const PIPELINE_ONLY: (keyof FormState)[] = ['line_number', 'od', 'wt', 'grade', 'mop'];
const FACILITY_ONLY: (keyof FormState)[] = ['facility_type'];

/** The write body: every shown field on create, only the changed ones on edit. */
export function siteWriteBody(form: FormState, initial: FormState | null): PfSiteWrite {
  const hidden = new Set<keyof FormState>(form.kind === 'facility' ? PIPELINE_ONLY : FACILITY_ONLY);
  const body: Record<string, unknown> = {};
  (Object.keys(form) as (keyof FormState)[]).forEach((k) => {
    if (hidden.has(k)) return;
    if (initial && form[k] === initial[k]) return;
    const v = form[k];
    if (typeof v === 'boolean') body[k] = v;
    else if (k === 'customer_id' || k === 'kind' || k === 'status') body[k] = v;
    else if (k === 'name') body[k] = v.trim();
    else body[k] = blankToNull(v);
  });
  if (!initial) {
    // A create sends no empty optional field at all: absent and null mean the same to the API.
    Object.keys(body).forEach((k) => body[k] === null && delete body[k]);
  }
  return body as PfSiteWrite;
}

export function SiteForm({
  site,
  catalog,
  meta,
  customers,
  pending,
  error,
  submitLabel,
  onSubmit,
  onCancel,
}: {
  site: PfSite | null;
  catalog: PfCatalog | null;
  meta: PfMeta | null;
  customers: { id: string; name: string }[];
  pending: boolean;
  error: string | null;
  submitLabel: string;
  onSubmit: (body: PfSiteWrite) => void;
  onCancel: () => void;
}) {
  const kinds = catalog?.siteKinds ?? [];
  const initial = useMemo(() => (site ? fromSite(site, site.kind) : null), [site]);
  const [form, setForm] = useState<FormState>(() => fromSite(site, kinds[0]?.value ?? 'pipeline'));
  const [localError, setLocalError] = useState<string | null>(null);
  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => setForm((f) => ({ ...f, [k]: v }));
  const units = meta?.units ?? null;
  const unit = (u: string | undefined) => (u ? ` (${u})` : '');
  const isFacility = form.kind === 'facility';
  const country = meta?.country === 'US' ? 'US' : 'CA';

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    if (!form.customer_id) return setLocalError('Pick the customer this site belongs to.');
    if (!form.name.trim()) return setLocalError('Give the site a name.');
    const body = siteWriteBody(form, initial);
    if (initial && Object.keys(body).length === 0) return setLocalError('Nothing has changed.');
    onSubmit(body);
  };

  return (
    <form onSubmit={submit} className="space-y-4 px-4 py-4" data-testid="pf-site-form" noValidate>
      <PfFormError message={localError ?? error} />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <PfField label="Customer">
          <select className={PF_INPUT} value={form.customer_id} onChange={(e) => set('customer_id', e.target.value)} aria-label="Customer">
            <option value="">Select a customer…</option>
            {/* The site's own customer stays selectable even if the list could not load. */}
            {site && !customers.some((c) => c.id === site.customerId) && (
              <option value={site.customerId}>{site.customerName ?? '—'}</option>
            )}
            {customers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </PfField>
        <PfField label="Kind">
          <select className={PF_INPUT} value={form.kind} onChange={(e) => set('kind', e.target.value)} aria-label="Kind">
            {kinds.map((k) => (
              <option key={k.value} value={k.value}>
                {k.label}
              </option>
            ))}
          </select>
        </PfField>
        <PfField label="Name">
          <input className={PF_INPUT} value={form.name} onChange={(e) => set('name', e.target.value)} aria-label="Name" />
        </PfField>
        <PfField label="Licence number">
          <input className={PF_INPUT} value={form.licence_number} onChange={(e) => set('licence_number', e.target.value)} aria-label="Licence number" />
        </PfField>
        {!isFacility && (
          <PfField label="Line number">
            <input className={PF_INPUT} value={form.line_number} onChange={(e) => set('line_number', e.target.value)} aria-label="Line number" />
          </PfField>
        )}
        {isFacility && (
          <PfField label="Facility type" hint="e.g. compressor station, battery">
            <input className={PF_INPUT} value={form.facility_type} onChange={(e) => set('facility_type', e.target.value)} aria-label="Facility type" />
          </PfField>
        )}
        <PfField label="LSD / location">
          <input className={PF_INPUT} value={form.lsd} onChange={(e) => set('lsd', e.target.value)} aria-label="LSD" />
        </PfField>
        <PfField label={country === 'US' ? 'State' : 'Province'}>
          <ProvinceSelect
            country={country}
            value={form.province_state}
            onChange={(v) => set('province_state', v)}
            className={PF_INPUT}
            aria-label={country === 'US' ? 'State' : 'Province'}
          />
        </PfField>
        <PfField label="Latitude">
          <input className={PF_INPUT} inputMode="decimal" value={form.latitude} onChange={(e) => set('latitude', e.target.value)} aria-label="Latitude" />
        </PfField>
        <PfField label="Longitude">
          <input className={PF_INPUT} inputMode="decimal" value={form.longitude} onChange={(e) => set('longitude', e.target.value)} aria-label="Longitude" />
        </PfField>
        <PfField label="Product">
          <select className={PF_INPUT} value={form.product} onChange={(e) => set('product', e.target.value)} aria-label="Product">
            <option value="">Not set</option>
            {(catalog?.products ?? []).map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </select>
        </PfField>
        {!isFacility && (
          <>
            <PfField label={`OD${unit(units?.diameter)}`}>
              <input className={PF_INPUT} inputMode="decimal" value={form.od} onChange={(e) => set('od', e.target.value)} aria-label="OD" />
            </PfField>
            <PfField label={`WT${unit(units?.wallThickness)}`} hint="Less than half of OD">
              <input className={PF_INPUT} inputMode="decimal" value={form.wt} onChange={(e) => set('wt', e.target.value)} aria-label="WT" />
            </PfField>
            <PfField label="Grade">
              <input className={PF_INPUT} value={form.grade} onChange={(e) => set('grade', e.target.value)} aria-label="Grade" />
            </PfField>
            <PfField label={`MOP${unit(units?.pressure)}`}>
              <input className={PF_INPUT} inputMode="decimal" value={form.mop} onChange={(e) => set('mop', e.target.value)} aria-label="MOP" />
            </PfField>
          </>
        )}
        {site && (
          <PfField label="Status">
            <select className={PF_INPUT} value={form.status} onChange={(e) => set('status', e.target.value)} aria-label="Status">
              {(catalog?.siteStatuses ?? []).map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </PfField>
        )}
        <label className="flex items-center gap-2 self-end pb-2 text-sm text-fieldiq-text">
          <input type="checkbox" checked={form.sour} onChange={(e) => set('sour', e.target.checked)} />
          Sour service (H2S)
        </label>
      </div>
      <PfField label="Notes">
        <textarea
          className={`${PF_INPUT} h-24 py-2`}
          value={form.notes}
          onChange={(e) => set('notes', e.target.value)}
          aria-label="Notes"
        />
      </PfField>
      <div className="flex flex-wrap justify-end gap-2">
        <button type="button" className={PF_SECONDARY} onClick={onCancel} disabled={pending}>
          Cancel
        </button>
        <button type="submit" className={PF_PRIMARY} disabled={pending}>
          {pending ? 'Saving…' : submitLabel}
        </button>
      </div>
    </form>
  );
}
